import React, { useState } from 'react';
import { ThemeProvider } from './contexts/ThemeContext';
import { FilterProvider } from './contexts/FilterContext';
import { Header } from './components/common';
import TrendsDashboard from './components/TrendsDashboard';
import TagDetailView from './components/TagDetailView';
import PlatformMediaView from './components/PlatformMediaView';
import MediaDetailView from './components/MediaDetailView';
import './index.css';

/**
 * Main App Component
 * 
 * Simple routing implementation without react-router for now.
 * When adding react-router, replace this with proper routes.
 * 
 * Views:
 * - media: YouTube videos view (default)
 * - dashboard: Trending tags view
 * - tag-detail: Tag detail view
 * - media-detail: Media detail view
 */
function App() {
  // Simple routing state
  // Default to YouTube Top Videos (Step 1 produces media_rankings + media_content)
  const [currentView, setCurrentView] = useState('media');
  const [viewParams, setViewParams] = useState({ platform: 'youtube', mediaType: 'video' });

  // Navigation functions - ready for react-router replacement
  const navigate = (view, params = {}) => {
    setCurrentView(view);
    setViewParams(params);
    // Scroll to top on navigation
    window.scrollTo(0, 0);
  };

  const goToTagDetail = (tagId, regionCode = 'GLOBAL') => {
    navigate('tag-detail', { tagId, regionCode });
  };

  const goToMediaView = (platform = 'youtube') => {
    navigate('media', { platform: 'youtube', mediaType: 'video' });
  };

  const goToMediaDetail = (trendId) => {
    navigate('media-detail', { trendId });
  };

  const goToDashboard = () => {
    navigate('dashboard');
  };

  // Handle header navigation
  const handleHeaderNavigate = (view) => {
    if (view === 'media') {
      goToMediaView();
    } else if (view === 'dashboard') {
      goToDashboard();
    }
  };

  // Get base view for header active state
  const getBaseView = () => {
    if (currentView === 'media-detail') return 'media';
    if (currentView === 'tag-detail') return 'dashboard';
    return currentView;
  };

  // Render current view
  const renderView = () => {
    switch (currentView) {
      case 'tag-detail':
        return (
          <TagDetailView 
            tagId={viewParams.tagId}
            regionCode={viewParams.regionCode || 'GLOBAL'}
            onBack={goToDashboard}
            onMediaClick={goToMediaDetail}
          />
        );
      case 'media':
        return (
          <PlatformMediaView
            initialPlatform={viewParams.platform}
            initialMediaType={viewParams.mediaType}
            onMediaClick={goToMediaDetail}
          />
        );
      case 'media-detail':
        return (
          <MediaDetailView
            trendId={viewParams.trendId}
            onBack={() => navigate('media')}
            onTagClick={goToTagDetail}
          />
        );
      case 'dashboard':
      default:
        return (
          <TrendsDashboard 
            onTagClick={goToTagDetail}
            onMediaViewClick={goToMediaView}
          />
        );
    }
  };

  return (
    <ThemeProvider>
      <FilterProvider>
        <div className="App">
          <Header 
            currentView={getBaseView()} 
            onNavigate={handleHeaderNavigate} 
          />
          <main className="app-main">
            {renderView()}
          </main>
        </div>
      </FilterProvider>
    </ThemeProvider>
  );
}

export default App;
